/**
 * 认证 Store
 *
 * 安全架构：
 * - Access Token：仅存储在内存（本 store），不做持久化
 * - Refresh Token：存储在 HttpOnly Cookie，由后端管理
 * - 用户元数据：存储在 localStorage，用于快速恢复 UI
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ConnectError, Code } from '@connectrpc/connect';
import type { User, AuthError } from '@/types/auth';
import { authStorage } from '@/services/auth/storage';
import { authClient } from '@/services/auth/client';
import {
  developmentAutoLoginService,
  type AttemptLoginResult,
} from '@/services/auth/DevelopmentAutoLoginService';
import type { LoginResponse } from '@/services/auth/contracts';
import { resolveError } from '@/services/api/error-handler';
import { authSessionExpiryNoticeService } from '@/services/auth/AuthSessionExpiryNoticeService';

/** initSession 超时时间（毫秒） */
const INIT_SESSION_TIMEOUT_MS = 15_000;

/** 开发环境自动登录失败在 AuthError 中使用的稳定错误码。 */
export const DEVELOPMENT_AUTO_LOGIN_FAILED_CODE = 'DEV_AUTO_LOGIN_FAILED';

/**
 * 会话刷新选项。
 */
export interface RefreshSessionOptions {
  /**
   * 是否允许 refresh 认证失败后尝试开发环境自动登录。
   *
   * initSession 需要自己控制超时后的状态落地，所以会关闭 refresh 内部兜底，
   * 再用同一个恢复函数配合 timedOut 检查完成自动登录。
   */
  allowDevelopmentAutoLoginFallback?: boolean;

  /**
   * 认证失败时是否展示运行中会话失效提示。
   *
   * 应用启动阶段会由路由守卫统一收口，不能弹出运行中会话失效提示。
   */
  notifyOnAuthFailure?: boolean;
}

interface DevelopmentAutoLoginRecoveryOptions {
  /**
   * 自动登录结果落地前的生命周期检查。
   *
   * 返回 false 表示调用方已经失效，例如 initSession 已超时，
   * 此时必须丢弃迟到结果，避免把 guest 状态再次改回登录态。
   */
  canApplyResult?: () => boolean;
}

/**
 * 判断错误是否为网络类错误（非认证失败）。
 * 网络错误不应立即进入 guest 状态，上层可决定是否重试。
 *
 * @param e 待分类的异常。
 * @returns 是否为网络类错误。
 */
function isNetworkError(e: unknown): boolean {
  // ConnectError 中 Unavailable / DeadlineExceeded / Canceled 视为网络问题。
  if (e instanceof ConnectError) {
    return (
      e.code === Code.Unavailable ||
      e.code === Code.DeadlineExceeded ||
      e.code === Code.Canceled
    );
  }
  // 非 ConnectError 的原生错误（如 fetch failed / TypeError）也视为网络问题。
  if (e instanceof TypeError) {
    return true;
  }
  return false;
}

/**
 * 判断自动登录结果是否代表“已启用但无法登录”的开发态错误。
 *
 * disabled / not-dev-env 表示该能力本来就不应该接管登录流程，
 * 只有凭据缺失或真实登录失败才需要阻止外部登录跳转并展示内部错误页。
 *
 * @param result 自动登录服务返回的结构化结果。
 * @returns 是否需要进入开发自动登录失败状态。
 */
function isDevelopmentAutoLoginFailure(result: AttemptLoginResult): boolean {
  if (result.status === 'failed') {
    return true;
  }
  return result.status === 'skipped' && result.reason === 'credentials-missing';
}

/**
 * 从自动登录结果生成可展示、可被路由识别的 AuthError。
 *
 * @param result 自动登录服务返回的结构化结果。
 * @returns 统一错误对象。
 */
function createDevelopmentAutoLoginError(result: AttemptLoginResult): AuthError {
  if (result.status === 'failed') {
    const message = result.error instanceof Error
      ? result.error.message
      : '开发环境自动登录请求失败';
    return {
      code: DEVELOPMENT_AUTO_LOGIN_FAILED_CODE,
      message,
      originalError: result.error,
    };
  }

  return {
    code: DEVELOPMENT_AUTO_LOGIN_FAILED_CODE,
    message: '开发环境自动登录失败：缺少用户名或密码配置',
    originalError: result,
  };
}

/**
 * 将 AuthError 转成 Promise rejection 使用的 Error。
 *
 * refresh interceptor 只需要知道刷新链路失败即可；真正用于页面展示的结构化信息
 * 已经存放在 store.error 中，因此这里保留清晰的人类可读消息。
 *
 * @param authError 结构化认证错误。
 * @returns 可抛出的错误对象。
 */
function toThrowableError(authError: AuthError): Error {
  return authError.originalError instanceof Error
    ? authError.originalError
    : new Error(authError.message);
}

export const useAuthStore = defineStore('auth', () => {
  // ── 状态 ─────────────────────────────────────
  const accessToken = ref<string | null>(null);
  const user = ref<User | null>(null);
  const isLoadingSession = ref(true);
  const error = ref<AuthError | null>(null);

  // 派生状态
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const hasDevelopmentAutoLoginFailure = computed(
    () => error.value?.code === DEVELOPMENT_AUTO_LOGIN_FAILED_CODE,
  );

  // ── 互斥锁 ──────────────────────────────────
  // 防重入：确保 initSession 只执行一次。
  let initPromise: Promise<void> | null = null;
  // 防重入：确保并发 refreshSession 复用同一个 Promise。
  let refreshPromise: Promise<string> | null = null;

  /**
   * 进入 guest 状态。
   *
   * 清除内存 token、用户信息与本地元数据，确保后续路由守卫按未登录状态运行。
   */
  function enterGuestMode() {
    user.value = null;
    accessToken.value = null;
    authStorage.clearMetadata();
  }

  /**
   * 会话落地：将登录响应写入内存状态与安全元数据。
   * Cookie 会话恢复与开发环境自动登录复用同一落地路径。
   *
   * @param response 登录响应。
   */
  function applyAuthenticatedSession(response: LoginResponse) {
    accessToken.value = response.accessToken;
    if (response.user) {
      user.value = response.user;
      authStorage.setMetadata({
        user: response.user,
        lastRefreshAt: Date.now(),
      });
    }
  }

  /**
   * 开发环境自动登录分支。
   * 仅在 Cookie 会话无法恢复时调用。
   * 返回结构化结果，由统一恢复函数决定是否落地会话。
   *
   * @returns 自动登录尝试结果。
   */
  async function tryDevelopmentAutoLogin(): Promise<AttemptLoginResult> {
    return developmentAutoLoginService.attemptLogin();
  }

  /**
   * 尝试使用开发环境自动登录恢复会话。
   *
   * @param options 生命周期约束，避免初始化超时后迟到写入。
   * @returns 成功恢复时的新 Access Token；未启用开发自动登录时返回 null。
   * @throws 自动登录已启用但失败时抛出明确错误，并把错误状态留在 store 中供路由消费。
   */
  async function recoverWithDevelopmentAutoLogin(
    options: DevelopmentAutoLoginRecoveryOptions = {},
  ): Promise<string | null> {
    const result = await tryDevelopmentAutoLogin();

    if (options.canApplyResult && !options.canApplyResult()) {
      return null;
    }

    if (result.status === 'success') {
      applyAuthenticatedSession(result.response);
      error.value = null;
      return result.response.accessToken;
    }

    if (isDevelopmentAutoLoginFailure(result)) {
      const authError = createDevelopmentAutoLoginError(result);
      enterGuestMode();
      error.value = authError;
      throw toThrowableError(authError);
    }

    return null;
  }

  /**
   * 应用启动或页面刷新时初始化会话。
   * 策略：
   * 1. 从 localStorage 快速恢复用户信息
   * 2. 通过 HttpOnly Cookie 刷新 Access Token
   * 3. 向后端校验 token 并获取最新用户信息
   *
   * 带超时保护：超过 INIT_SESSION_TIMEOUT_MS 则放弃并进入 guest 状态。
   *
   * @returns 初始化流程 Promise。
   */
  async function initSession() {
    // 如果已经在执行中，返回现有的 Promise。
    if (initPromise) {
      return initPromise;
    }

    // 迁移：清理旧版 token 存储。
    authStorage.migrateFromLegacy();

    // 超时保护标志：超时后阻止内层 IIFE 覆盖 guest 状态。
    let timedOut = false;

    initPromise = (async () => {
      isLoadingSession.value = true;
      error.value = null;

      try {
        // 1. 快速恢复：从 localStorage 加载用户信息。
        const metadata = authStorage.getMetadata();
        if (metadata?.user) {
          user.value = metadata.user;
        }

        // 2. 基于 Cookie 的会话恢复。
        try {
          await refreshSession({
            allowDevelopmentAutoLoginFallback: false,
            notifyOnAuthFailure: false,
          });
        } catch {
          const recoveredToken = await recoverWithDevelopmentAutoLogin({
            canApplyResult: () => !timedOut,
          });
          if (timedOut) return;
          if (!recoveredToken) {
            /**
             * 自动登录没有成功接管时，必须显式收口到 guest。
             *
             * 原因：
             * 1. 保持与 testGLB 时代“会话恢复失败 -> guest”语义一致。
             * 2. 避免网络错误场景下保留 localStorage 恢复出的半初始化 user。
             * 3. 防止自动登录关闭后，旧登录链路被残留状态干扰。
             */
            enterGuestMode();
            return;
          }
          // 自动登录成功后继续进入 getProfile 校准。
        }

        if (timedOut) return;

        // 3. 校验 token 有效性并刷新用户信息。
        try {
          const profile = await authClient.getProfile({});
          if (timedOut) return;
          user.value = profile;

          authStorage.setMetadata({
            user: profile,
            lastRefreshAt: Date.now(),
          });
        } catch (e) {
          if (timedOut) return;
          // token 校验失败时进入 guest，并记录可展示错误。
          enterGuestMode();
          error.value = resolveError(e);
        }
      } catch (e) {
        if (timedOut) return;
        if (hasDevelopmentAutoLoginFailure.value) {
          return;
        }
        error.value = resolveError(e);
        enterGuestMode();
      } finally {
        if (!timedOut) {
          isLoadingSession.value = false;
        }
        initPromise = null;
      }
    })();
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => {
        timedOut = true;
        reject(new Error('initSession 超时'));
      }, INIT_SESSION_TIMEOUT_MS),
    );

    initPromise = Promise.race([initPromise, timeout]).catch(() => {
      if (timedOut) {
        enterGuestMode();
        isLoadingSession.value = false;
      }
      initPromise = null;
    });

    return initPromise;
  }

  /**
   * 登出：清除所有会话数据并通知后端。
   *
   * @returns 登出流程 Promise。
   */
  async function logout() {
    try {
      await authClient.logout({});
    } catch (_e) {
      // 即使服务端调用失败，仍然清除本地会话。
    } finally {
      accessToken.value = null;
      user.value = null;
      authStorage.clearMetadata();
      error.value = null;
    }
  }

  /**
   * 刷新会话：通过 HttpOnly Cookie 获取新的 Access Token。
   * 调用方：
   * - initSession()（页面加载时）
   * - refreshInterceptor（Access Token 过期 401 时）
   *
   * 互斥：
   * - 如果 initSession 正在执行，不会同时发起 refresh
   *   （initSession 内部会调用 refreshSession，外部不需要重复调用）
   * - 并发 refreshSession 请求会复用同一个 Promise
   *
   * 错误分类：
   * - 网络错误 → 保留当前状态，向上层抛出异常
   * - 认证失败 → 优先尝试开发环境自动登录；没有接管时清除 token 并进入 guest 状态
   *
   * @param options 会话刷新选项。
   * @returns 新的 Access Token。
   */
  async function refreshSession(options: RefreshSessionOptions = {}): Promise<string> {
    // 如果已有进行中的 refresh，复用同一个 Promise。
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        // 请求体为空，后端通过 Cookie 读取 Refresh Token。
        const newTokens = await authClient.refreshTokens({});

        // 将新 Access Token 存入内存。
        accessToken.value = newTokens.accessToken;
        // refresh 已恢复会话时清除旧错误，避免开发自动登录失败标记残留影响后续导航。
        error.value = null;

        // 更新元数据时间戳。
        const metadata = authStorage.getMetadata();
        if (metadata) {
          authStorage.setMetadata({
            ...metadata,
            lastRefreshAt: Date.now(),
          });
        }

        return newTokens.accessToken;
      } catch (e) {
        if (isNetworkError(e)) {
          // 网络错误：保留当前状态，不清除 token，让上层决定是否重试。
          throw e;
        }

        const shouldAttemptDevelopmentAutoLogin =
          options.allowDevelopmentAutoLoginFallback ?? true;

        if (shouldAttemptDevelopmentAutoLogin) {
          const recoveredToken = await recoverWithDevelopmentAutoLogin();
          if (recoveredToken) {
            return recoveredToken;
          }
        }

        // 认证失败（401 / invalid_grant 等）且没有开发自动登录接管：保持原有登出语义。
        await logout();
        if (options.notifyOnAuthFailure ?? true) {
          /**
           * 运行中接口触发 refresh 失败时，需要显式提示用户登录已失效；
           * 应用首次初始化的失败由路由守卫统一跳转，避免启动阶段重复弹窗。
           */
          authSessionExpiryNoticeService.notifySessionExpired();
        }
        throw e;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  /**
   * 获取当前 Access Token（供 auth interceptor 使用）。
   *
   * @returns 当前内存中的 Access Token。
   */
  function getAccessToken(): string | null {
    return accessToken.value;
  }

  return {
    // 状态（只读导出）
    user,
    isAuthenticated,
    isLoadingSession,
    error,
    hasDevelopmentAutoLoginFailure,

    // 操作
    initSession,
    logout,
    refreshSession,

    // 供 auth interceptor 使用
    getAccessToken,
  };
});
